"use client"

import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card } from "@/components/ui/card"
import { Loader2, Sparkles, PlusCircle, Trash2, FileDown, ChevronDown, Settings } from "lucide-react"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { downloadExcel, downloadExcelWithTemplate, CustomerRow } from "@/utils/excelUtils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import DeliveryTemplateManager from "@/components/DeliveryTemplateManager"
import type { DeliveryTemplate } from "@/utils/templateParser"
import * as deliveryTemplateService from "@/supabase/deliverytemplate/service/deliveryTemplateService"
import { supabase } from "@/supabase/client"
import { DatePicker } from "@/components/DatePicker"
import { format, addDays, subDays, isSameDay, parseISO } from "date-fns"
import { useAuth } from "@/hooks/useAuth"
import { useLoginModal } from "@/contexts/LoginModalContext"
import { logUsage } from '@/lib/usageLog'
import { checkNonMemberUsage, incrementNonMemberUsage } from '@/lib/pseudoUser'
import { PaymentService } from '@/supabase/payment/paymentService'
import { toast } from 'sonner'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { logError } from "@/lib/sentry"
import { useParsingOptions } from "@/hooks/useParsingOptions"
import { isEmptyFieldValue, formatPhoneNumber } from "@/lib/utils"
import { ShippingStaleSyncContainer } from "@/components/ShippingStaleSyncContainer"
import { Checkbox } from "@/components/ui/checkbox"
import { useExcelKeepInfo } from "@/hooks/useExcelKeepInfo"
import { useMemberPreference } from "@/hooks/useMemberPreference"
import { sortShippingRows } from "@/utils/shippingRowSort"
import { ShippingSortHeaderMenu } from "@/components/ShippingSortHeaderMenu"

interface ColumnWidth {
  checkbox: number
  orderNum: number
  name: number
  nickname: number
  phone: number
  address: number
  deliveryNotes: number
  productName: number; // 상품명 컬럼 너비 추가
  actions: number
}

export default function NewExcel() {
  const [customerText, setCustomerText] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [newRowIndex, setNewRowIndex] = useState<number | null>(null)
  const [rows, setRows] = useState<CustomerRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [editingRowId, setEditingRowId] = useState<number | null>(null)
  const [editingField, setEditingField] = useState<'name' | 'nickname' | 'phone' | 'address' | 'deliveryNotes' | 'productName' | null>(null) // productName 추가
  const [editFormData, setEditFormData] = useState<CustomerRow | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  // useEffect[user] 클로저의 visibility 핸들러가 최신 선택 날짜를 읽기 위한 latest-ref.
  // 없으면 날짜 변경 후 탭 복귀 시 stale 날짜로 rows를 재조회함 (codexloop 델타 리뷰 지적, 2026-07-11).
  const selectedDateRef = useRef(selectedDate)
  useLayoutEffect(() => {
    selectedDateRef.current = selectedDate
  })
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([])
  const [columnWidths, setColumnWidths] = useState<ColumnWidth>({
    checkbox: 4,
    orderNum: 3,
    nickname: 12,
    name: 10,
    phone: 13,
    address: 25,
    productName: 25,
    deliveryNotes: 20,
    actions: 4
  })
  const [resizingColumn, setResizingColumn] = useState<keyof ColumnWidth | null>(null)
  const [startX, setStartX] = useState(0)
  const [startWidth, setStartWidth] = useState(0)
  const tableRef = useRef<HTMLTableElement>(null)
  const inputRefs = {
    name: useRef<HTMLInputElement>(null),
    nickname: useRef<HTMLInputElement>(null),
    phone: useRef<HTMLInputElement>(null),
    address: useRef<HTMLInputElement>(null),
    deliveryNotes: useRef<HTMLInputElement>(null),
    productName: useRef<HTMLTextAreaElement>(null) // 상품명 입력 ref (textarea)
  }
  const { user } = useAuth()
  const { openLoginModal } = useLoginModal()
  const [paymentService] = useState(() => new PaymentService())
  const [showUsageLimitDialog, setShowUsageLimitDialog] = useState(false)
  const [showNonMemberLimitDialog, setShowNonMemberLimitDialog] = useState(false)
  const [migrationCompleted, setMigrationCompleted] = useState(false)
  const [migrationCheckInterval, setMigrationCheckInterval] = useState<NodeJS.Timeout | null>(null)
  const { parsingOptions, setOptions: setParsingOptions } = useParsingOptions();
  const [isKeepDownloading, setIsKeepDownloading] = useState(false)
  const [downloadMode, setDownloadMode] = useState<'all' | 'all_exclude_keep' | 'keep4w'>('all')
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false)
  const emptyGuideRef = useRef<HTMLDivElement>(null)

  // 일괄 삭제 관련 상태
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set())
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  // 택배사 양식 관련 상태
  const [savedTemplate, setSavedTemplate] = useState<DeliveryTemplate | null>(null)
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)

  // 세대 가드: 늦게 도착한 이전 fetch 응답이 최신 rows(반영 패치 포함)를 통째로 되돌리는
  // 것을 막는다 (stale 일괄 반영 도입으로 실害가 생겨 targeted 수정 — PR2 리뷰 지적).
  // 동기 패치(반영/재조정)도 시작 전에 세대를 올려 pending fetch를 무효화한다 — 안 그러면
  // "패치 → 늦은 fetch 도착 → 이전 스냅샷으로 전체 교체"로 패치가 되돌아간다.
  const fetchRowsSeqRef = useRef(0)
  // 대체 refetch (구현 델타 합의): 패치의 세대 증가가 in-flight fetch를 폐기하면, 그 응답에
  // 실려 있었을 다른 탭의 추가/수정이 다음 트리거까지 화면·다운로드에서 빠진다. 폐기 사실만
  // 플래그로 기록하고, 반영이 완전히 끝난 시점(busy 해제)에 한 번만 보충 조회한다 —
  // ambiguous 복구의 다회 onReconciled도 플래그라 자연 코얼레싱된다.
  const fetchRowsActiveRef = useRef(0)
  const staleSyncRefetchNeededRef = useRef(false)

  // stale 상품명 일괄 반영 — 반영 중 다운로드 차단 플래그 + applied 행 패치 콜백.
  // 로직 전체는 ShippingStaleSyncContainer/useShippingStaleSync에 격리(안티블로트).
  const [staleSyncBusy, setStaleSyncBusy] = useState(false)
  // 같은 스코프에서 후보 신선도에 영향 주는 페이지측 변경(인라인 상품명 저장) 시 증가 →
  // 컨테이너 재스캔 트리거 (PR 리뷰 P1: 스코프 동일 변경 미감지)
  const [staleSyncRefreshToken, setStaleSyncRefreshToken] = useState(0)
  const handleStaleSyncApplied = useCallback(
    (applied: { parsedOrderId: number; newValue: string }[]) => {
      if (applied.length === 0) return
      fetchRowsSeqRef.current += 1 // pending fetch 무효화 (위 주석)
      if (fetchRowsActiveRef.current > 0) staleSyncRefetchNeededRef.current = true
      // CustomerRow.id == parsed_order.id — 현재 화면에 존재하는 id만 갱신(합의 계약).
      const byId = new Map(applied.map((a) => [a.parsedOrderId, a.newValue]))
      setRows((prev) =>
        prev.map((row) => {
          const next = byId.get(row.id)
          return next !== undefined ? { ...row, productName: next } : row
        })
      )
    },
    []
  )
  // commit-ambiguous 복구: DB가 실제로 커밋됐는데 응답만 유실된 경우, 복구 스캔이 읽은
  // DB 확정값으로 화면 rows의 productName을 재조정 — "배너 없음 + 로컬 stale 다운로드" 차단.
  const handleStaleSyncReconciled = useCallback(
    (dbRows: { parsedOrderId: number; productName: string | null }[]) => {
      if (dbRows.length === 0) return
      fetchRowsSeqRef.current += 1 // pending fetch 무효화
      if (fetchRowsActiveRef.current > 0) staleSyncRefetchNeededRef.current = true
      const byId = new Map(dbRows.map((r) => [r.parsedOrderId, r.productName]))
      setRows((prev) =>
        prev.map((row) => {
          if (!byId.has(row.id)) return row
          const dbValue = byId.get(row.id) ?? ""
          const displayValue = isEmptyFieldValue(dbValue) ? "" : dbValue
          return row.productName === displayValue ? row : { ...row, productName: displayValue }
        })
      )
    },
    []
  )
  // 보충 refetch 소비 — 콜백이 아니라 effect에서 (구현 델타 리뷰, 양 리뷰어 동일 지적):
  // 콜백에서 소비하면 로그아웃 언마운트 cleanup이 "한 커밋 전" 클로저(구 uid·auth=true)로
  // refetch를 발사할 수 있다. 세션이 정리된 뒤의 RLS 응답은 200+빈 배열(성공)이라
  // preserveOnError가 못 막고 게스트 화면을 빈 상태로 덮는다. deps 없는 effect는 매 커밋
  // "신선한 렌더 값"을 읽으므로 로그아웃 커밋에서 자연 차단되고(라우트 언마운트도 무발사),
  // busy 해제(반영·검증·복구·지연 큐 소화 완료) 후 첫 커밋에서 한 번만 소비된다.
  useEffect(() => {
    if (!staleSyncBusy && staleSyncRefetchNeededRef.current) {
      staleSyncRefetchNeededRef.current = false
      if (currentUserIsAuthenticated && currentUserId) {
        fetchRowsByDate(currentUserId, selectedDateRef.current, { preserveOnError: true })
      }
    }
  })

  // 항상 안전한 pseudoUserId 사용
  const getPseudoUserId = (): string => {
    // 1. 스토리지에서 직접 가져오기
    let storedId = sessionStorage.getItem("pseudoUserId");
    
    // 2. 없으면 새로 생성
    if (!storedId) {
      storedId = crypto.randomUUID();
      console.log(`[NewExcel:getPseudoUserId] 새 ID 생성: ${storedId}`);
      sessionStorage.setItem("pseudoUserId", storedId);
    } else {
      console.log(`[NewExcel:getPseudoUserId] 스토리지에서 불러옴: ${storedId}`);
    }
    
    return storedId;
  }
  
  const currentUserId = user?.id || null
  const currentUserIsAuthenticated = !!user

  // 배송엑셀 정렬 기준(계정 저장) — 표·다운로드는 displayRows, 행 변경은 rows로 한다.
  // 스펙: docs/superpowers/specs/2026-10-06-shipping-excel-settlement-order-design.md
  const { shippingExcelSort, preferenceStatus, setShippingExcelSort } = useMemberPreference({ user })
  const effectiveShippingSort = preferenceStatus === 'error' ? 'entry' : shippingExcelSort
  const displayRows = useMemo(() => sortShippingRows(rows, effectiveShippingSort), [rows, effectiveShippingSort])
  const isSortPrefLoading = currentUserIsAuthenticated && preferenceStatus === 'loading'

  // 킵 배지 + 킵 제외 다운로드 (회원 전용) — 스펙: docs/superpowers/specs/2026-07-10-excel-keep-exclude-design.md
  // isKeepInfoLoading/isKeepInfoError는 훅 내부(handleExcludeKeepExcelDownload)에서만 소비되고
  // NewExcel.tsx에서는 미사용 — tsc noUnusedLocals(TS6133) 회피를 위해 미구조분해 (task-4-brief Step 3 contingency)
  const {
    keepNicknames,
    handleExcludeKeepExcelDownload,
  } = useExcelKeepInfo({
    userId: currentUserId,
    orderDate: format(selectedDate, "yyyy-MM-dd"),
    enabled: currentUserIsAuthenticated,
    rowsLoading: isLoading,
    rows: displayRows,
    parsingOptions,
    getPseudoUserId,
    setError,
    openLoginModal,
  })

  const handleOpenTemplateDialog = () => {
    if (!currentUserIsAuthenticated || !currentUserId) {
      toast.info('로그인 후 이용 가능한 기능입니다.')
      openLoginModal()
      return
    }
    setShowTemplateDialog(true)
  }

  // 로그인된 유저의 배송 템플릿 로드
  useEffect(() => {
    if (!currentUserId) {
      setSavedTemplate(null)
      return
    }
    deliveryTemplateService.getTemplates(currentUserId).then((templates) => {
      setSavedTemplate(templates.length > 0 ? templates[0] : null)
    }).catch(() => {
      setSavedTemplate(null)
    })
  }, [currentUserId])

  // 회원가입 후 마이그레이션 완료 체크하는 함수
  const checkMigrationStatus = async (userId: string, targetDate: string | null = null) => {
    console.log(`[NewExcel:checkMigrationStatus] userId: ${userId}, targetDate: ${targetDate}`);
    try {
      // 마이그레이션 중인지 localStorage 체크
      const migrationStatus = localStorage.getItem('migrationFlow');
      if (migrationStatus !== 'in_progress') {
        console.log('[NewExcel:checkMigrationStatus] No migration in progress.');
        clearMigrationCheck();
        setMigrationCompleted(true); // 명시적으로 마이그레이션 완료 상태로 설정
        return true; // 마이그레이션 없음 = 완료됨으로 간주
      }
      
      // 특정 날짜에 대한 데이터가 있는지 DB에서 확인
      if (targetDate) {
        const { data, error } = await supabase
          .from('parsed_order')
          .select('id, nickname, name, phone_number, address, delivery_notes, product_name, row_order') // product_name 추가
          .eq('user_id', userId)
          .eq('order_date', targetDate)
          // 화면 표시 순서 안정화: row_order(사용자 의도) -> created_at(생성 순) -> id(타이브레이커)
          .order('row_order', { ascending: true })
          .order('created_at', { ascending: true })

        if (error) {
          console.error('[NewExcel:checkMigrationStatus] Error checking migration status:', error);
          return false;
        }

        // 데이터가 있으면 마이그레이션 완료로 간주
        if (data && data.length > 0) {
          console.log(`[NewExcel:checkMigrationStatus] Found ${data.length} rows for ${targetDate}, migration completed.`);
          
          // 즉시 데이터 반영 (선택적)
          const formattedRows = data.map(item => ({
            id: Number(item.id),
            name: isEmptyFieldValue(item.name) ? "" : item.name,
            nickname: isEmptyFieldValue(item.nickname) ? "" : item.nickname,
            phone: isEmptyFieldValue(item.phone_number) ? "" : item.phone_number,
            address: isEmptyFieldValue(item.address) ? "" : item.address,
            deliveryNotes: isEmptyFieldValue(item.delivery_notes || "") ? "" : (item.delivery_notes || ""),
            productName: isEmptyFieldValue(item.product_name || "") ? "" : (item.product_name || ""),
            row_order: item.row_order
          }));
          
          // 명시적으로 즉시 UI 상태 업데이트
          setRows(formattedRows);
          setShowEmptyState(false);
          setMigrationCompleted(true); // 마이그레이션 완료 상태로 설정
          
          // localStorage 플래그 제거
          localStorage.removeItem('migrationFlow');
          localStorage.removeItem('migrationFocusDate');
          
          clearMigrationCheck();
          
          // 미리 하이라이트 날짜도 로드 시도
          fetchDatesWithData(userId).catch(err => {
            console.error('[NewExcel:checkMigrationStatus] Error fetching dates with data:', err);
          });
          
          return true;
        }
      } else {
        // 날짜가 없으면 전체 데이터 체크
        const { data, error } = await supabase
          .from('parsed_order')
          .select('id')
          .eq('user_id', userId)
          .limit(1);

        if (error) {
          console.error('[NewExcel:checkMigrationStatus] Error checking migration status:', error);
          return false;
        }

        // 데이터가 있으면 마이그레이션 완료로 간주
        if (data && data.length > 0) {
          console.log(`[NewExcel:checkMigrationStatus] Found some data, migration completed.`);
          localStorage.removeItem('migrationFlow');
          localStorage.removeItem('migrationFocusDate');
          setMigrationCompleted(true); // 마이그레이션 완료 상태로 설정
          clearMigrationCheck();
          return true;
        }
      }
      
      console.log('[NewExcel:checkMigrationStatus] Migration still in progress or no data found.');
      return false; // 여전히 마이그레이션 중 또는 데이터 없음
    } catch (err) {
      console.error('[NewExcel:checkMigrationStatus] Error:', err);
      return false;
    }
  };

  // 인터벌 클리어 함수
  const clearMigrationCheck = () => {
    if (migrationCheckInterval) {
      clearInterval(migrationCheckInterval);
      setMigrationCheckInterval(null);
      console.log('[NewExcel:clearMigrationCheck] Migration check interval cleared.');
    }
  };

  // 로그인 상태 확인
  useEffect(() => {
    console.log(`[NewExcel:useEffect[user]] User: ${currentUserId}, Auth: ${currentUserIsAuthenticated}`);
    
    const initializeData = async () => {
      setIsLoading(true);
      console.log('[NewExcel:initializeData] START');

      if (currentUserIsAuthenticated && currentUserId) {
        // 회원인 경우
        console.log(`[NewExcel:initializeData] User authenticated. userId: ${currentUserId}`);
        
        // localStorage에서 마이그레이션 상태 확인
        const migrationStatus = localStorage.getItem('migrationFlow');
        const focusDateStr = localStorage.getItem('migrationFocusDate');
        console.log(`[NewExcel:initializeData] migrationFlow: ${migrationStatus}, focusDate: ${focusDateStr}`);
        
        if (migrationStatus === 'in_progress') {
          // 마이그레이션 진행 중 - 주기적으로 완료 여부 체크
          console.log('[NewExcel:initializeData] Migration in progress, setting up check interval.');
          
          // 초기 체크
          const initialCheck = await checkMigrationStatus(currentUserId, focusDateStr || null);
          if (initialCheck) {
            // 이미 완료되어 있으면 데이터 로드
            console.log('[NewExcel:initializeData] Migration already completed in initial check.');
            setMigrationCompleted(true);
            
            // 마이그레이션 포커스 날짜가 있으면 해당 날짜로 설정
            if (focusDateStr) {
              const focusDate = parseISO(focusDateStr);
              if (!isNaN(focusDate.getTime()) && !isSameDay(selectedDate, focusDate)) {
                console.log(`[NewExcel:initializeData] Setting selectedDate to focusDate: ${focusDate}`);
                setSelectedDate(focusDate);
                await fetchRowsByDate(currentUserId, focusDate);
              } else {
                await fetchRowsByDate(currentUserId, selectedDate);
              }
            } else {
              await fetchRowsByDate(currentUserId, selectedDate);
            }
            
            await fetchDatesWithData(currentUserId);
            setIsLoading(false);
          } else {
            // 아직 진행 중이면 인터벌 설정
            setIsLoading(true); // 로딩 상태 유지
            console.log('[NewExcel:initializeData] Setting up interval to check migration status.');
            
            // 이전 인터벌 클리어
            clearMigrationCheck();
            
            toast.loading('데이터를 처리하는 중입니다...', { id: 'migration_wait' });
            
            // 마이그레이션 완료 체크 인터벌 설정 (1초마다)
            const intervalId = setInterval(async () => {
              try {
                const completed = await checkMigrationStatus(currentUserId, focusDateStr || null);
                if (completed) {
                  console.log('[NewExcel:migrationCheckInterval] Migration completed during interval check.');
                  setMigrationCompleted(true);
                  toast.dismiss('migration_wait');
                  toast.success('데이터 이전이 완료되었습니다.', { id: 'migration_complete', duration: 3000 });
                  
                  // 마이그레이션 완료 시 데이터 로드
                  if (focusDateStr) {
                    const focusDate = parseISO(focusDateStr);
                    if (!isNaN(focusDate.getTime()) && !isSameDay(selectedDate, focusDate)) {
                      console.log(`[NewExcel:migrationCheckInterval] Setting selectedDate to focusDate: ${focusDate}`);
                      setSelectedDate(focusDate);
                      await fetchRowsByDate(currentUserId, focusDate);
                    } else {
                      await fetchRowsByDate(currentUserId, selectedDate);
                    }
                  } else {
                    await fetchRowsByDate(currentUserId, selectedDate);
                  }
                  
                  await fetchDatesWithData(currentUserId);
                  setIsLoading(false);
                  clearMigrationCheck(); // 인터벌 클리어 재확인
                }
              } catch (intervalError) {
                console.error('[NewExcel:migrationCheckInterval] Error during migration check:', intervalError);
              }
            }, 1000); // 1초마다 체크
            
            setMigrationCheckInterval(intervalId);
            
            // 안전장치: 최대 30초 후 타임아웃
            setTimeout(() => {
              if (migrationCheckInterval) {
                console.log('[NewExcel:initializeData] Migration check timed out after 30s');
                clearMigrationCheck();
                toast.dismiss('migration_wait');
                toast.error('데이터 이전이 지연되고 있습니다. 페이지를 새로고침해보세요.');
                
                // 마이그레이션 상태 초기화
                localStorage.removeItem('migrationFlow');
                localStorage.removeItem('migrationFocusDate');
                setMigrationCompleted(true);
                
                // 최종 데이터 로드 시도
                fetchRowsByDate(currentUserId, selectedDate)
                  .then(() => fetchDatesWithData(currentUserId))
                  .catch(err => console.error('[NewExcel:timeout] Error fetching data:', err))
                  .finally(() => {
                    setIsLoading(false);
                  });
              }
            }, 30000);
          }
        } else {
          // 마이그레이션 진행 중이 아님 - 바로 데이터 로드
          console.log('[NewExcel:initializeData] No migration in progress, loading data directly.');
          setMigrationCompleted(true); // Ensure migrationCompleted is true if no migration was in progress
          await fetchRowsByDate(currentUserId, selectedDate);
          await fetchDatesWithData(currentUserId);
          setIsLoading(false);
        }
      } else {
        // 비회원인 경우
        console.log('[NewExcel:initializeData] User not authenticated, loading non-member data.');
        loadNonMemberData();
        setIsLoading(false);
      }
      
      console.log('[NewExcel:initializeData] END');
    };

    initializeData();

    // Auth 상태 변경 리스너
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const newUserId = session?.user?.id;
        console.log(`[NewExcel:onAuthStateChange] event: ${_event}, newUserId: ${newUserId}, currentUserId: ${currentUserId}`);
        
        // userId 변경 시 (로그인/로그아웃) useEffect[user]가 다시 실행됨
      }
    );

    // 브라우저 탭 활성화 시 처리
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[NewExcel:visibilityChange] Tab visible again');
        if (currentUserIsAuthenticated && currentUserId) {
          fetchRowsByDate(currentUserId, selectedDateRef.current);
          fetchDatesWithData(currentUserId);
        } else {
          loadNonMemberData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 클린업 함수
    return () => {
      authListener.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearMigrationCheck(); // 인터벌 클리어
      console.log('[NewExcel:useEffect[user]] Cleanup completed.');
    };
  }, [user]); // user가 변경될 때만 실행

  // selectedDate 변경 시 데이터 로드
  useEffect(() => {
    setSelectedRowIds(new Set())
    console.log(`[NewExcel:useEffect[selectedDate]] selectedDate: ${selectedDate}, isLoading: ${isLoading}, migrationCompleted: ${migrationCompleted}`);

    // 로딩 중이거나 마이그레이션 완료되지 않은 경우 무시
    if (isLoading || (currentUserIsAuthenticated && !migrationCompleted)) {
      console.log('[NewExcel:useEffect[selectedDate]] Loading or migration not completed, skipping.');
      return;
    }
    
    if (currentUserIsAuthenticated && currentUserId) {
      console.log('[NewExcel:useEffect[selectedDate]] Loading data from DB for selected date.');
      setIsLoading(true);
      fetchRowsByDate(currentUserId, selectedDate).finally(() => {
        setIsLoading(false);
      });
    } else {
      console.log('[NewExcel:useEffect[selectedDate]] Loading non-member data for selected date.');
      loadNonMemberData();
    }
  }, [selectedDate, migrationCompleted]);

  // rows 변경 시 존재하지 않는 ID를 선택에서 제거
  useEffect(() => {
    setSelectedRowIds(prev => {
      if (prev.size === 0) return prev
      const currentIds = new Set(rows.map(r => r.id))
      const cleaned = new Set([...prev].filter(id => currentIds.has(id)))
      return cleaned.size === prev.size ? prev : cleaned
    })
  }, [rows])

  // 비회원 최초 진입 시 가이드 영역으로 부드럽게 스크롤
  useEffect(() => {
    if (!isLoading && !currentUserIsAuthenticated && showEmptyState && !hasAutoScrolled) {
      setHasAutoScrolled(true);
      // 약간의 지연 후 스크롤(레이아웃 안정화 대기)
      setTimeout(() => {
        const target = emptyGuideRef.current;
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // 시작 위치로 스크롤 후, 안내 리스트가 충분히 보이도록 추가 오프셋
          setTimeout(() => {
            window.scrollBy({ top: 30, behavior: 'smooth' });
          }, 300);
        } else {
          // 폴백: 고정 오프셋으로 스크롤
          window.scrollTo({ top: 30, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [isLoading, currentUserIsAuthenticated, showEmptyState, hasAutoScrolled]);

  // DB에서 날짜별 데이터 로드
  const fetchRowsByDate = async (
    uid: string,
    date: Date,
    // preserveOnError: 대체(보충) 조회 전용 — 실패해도 화면의 사용 가능한 표(패치 반영분)를
    // 지우지 않고 토스트로만 알린다(구현 델타 합의 — 백그라운드 갱신은 비파괴).
    opts?: { preserveOnError?: boolean }
  ) => {
    console.log(`[NewExcel:fetchRowsByDate] START - uid: ${uid}, date: ${date}`);
    const fetchSeq = ++fetchRowsSeqRef.current;
    fetchRowsActiveRef.current += 1;
    try {
      const formattedDate = format(date, "yyyy-MM-dd");

      const { data, error } = await supabase
        .from("parsed_order")
        .select("*")
        .eq("user_id", uid)
        .eq("order_date", formattedDate)
        // 화면 표시 순서 안정화: row_order(사용자 의도) -> created_at(생성 순) -> id(타이브레이커)
        .order("row_order", { ascending: true })
        .order("created_at", { ascending: true })
        .order("id", { ascending: true })

      if (error) throw error;
      if (fetchSeq !== fetchRowsSeqRef.current) {
        console.log('[NewExcel:fetchRowsByDate] Stale response discarded.');
        return;
      }

      console.log(`[NewExcel:fetchRowsByDate] Fetched ${data?.length || 0} rows from DB.`);

      if (data && data.length > 0) {
        const formattedRows = data.map(item => ({
          id: Number(item.id),
          name: isEmptyFieldValue(item.name) ? "" : item.name,
          nickname: isEmptyFieldValue(item.nickname) ? "" : item.nickname,
          phone: isEmptyFieldValue(item.phone_number) ? "" : item.phone_number,
          address: isEmptyFieldValue(item.address) ? "" : item.address,
          deliveryNotes: isEmptyFieldValue(item.delivery_notes || "") ? "" : (item.delivery_notes || ""),
          productName: isEmptyFieldValue(item.product_name || "") ? "" : (item.product_name || ""),
          row_order: item.row_order,
          order_id: item.order_id ?? null
        }));
        
        // 콘솔에 각 행의 데이터 로깅 (디버깅용)
        console.log(`[NewExcel:fetchRowsByDate] Formatted rows (${formattedRows.length}):`);
        formattedRows.forEach((row, index) => {
          console.log(`Row ${index + 1}: id=${row.id}, nickname=${row.nickname}, name=${row.name}, productName=${row.productName}`);
        });
        
        setRows(formattedRows);
        setShowEmptyState(false);
        console.log(`[NewExcel:fetchRowsByDate] Set ${formattedRows.length} rows to state.`);
      } else {
        setRows([]);
        setShowEmptyState(true);
        console.log('[NewExcel:fetchRowsByDate] No data found, showing empty state.');
      }
    } catch (err: any) {
      logError("Error fetching data:", err);
      if (fetchSeq === fetchRowsSeqRef.current) {
        if (opts?.preserveOnError) {
          toast.error("배송정보를 다시 불러오지 못했습니다.\n새로고침으로 확인이 필요합니다.");
        } else {
          setError("데이터를 불러오는 중 오류가 발생했습니다.");
          setRows([]);
          setShowEmptyState(true);
        }
      }
      console.error('[NewExcel:fetchRowsByDate] Error:', err);
    } finally {
      fetchRowsActiveRef.current -= 1;
    }
    console.log('[NewExcel:fetchRowsByDate] END');
  };

  // 비회원 사용자 데이터 로드 함수
  const loadNonMemberData = () => {
    setIsLoading(true);
    try {
      // 항상 최신 pseudoUserId 사용
      const currentPseudoUserId = getPseudoUserId();
      
      // 선택된 날짜를 문자열로 변환
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      console.log(`[NewExcel:loadNonMemberData] 데이터 로드 시도 - 날짜: ${formattedDate}, pseudoUserId: ${currentPseudoUserId}`);
      
      // sessionStorage에서 데이터 로드
      const storageKey = `parsed_order_${currentPseudoUserId}_${formattedDate}`;
      console.log(`[NewExcel:loadNonMemberData] 스토리지 키: ${storageKey}`);
      
      // 디버깅: sessionStorage의 모든 키 출력
      const allKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) allKeys.push(key);
      }
      console.log(`[NewExcel:loadNonMemberData] 세션 스토리지 모든 키:`, allKeys);
      
      const savedData = sessionStorage.getItem(storageKey);
      
      if (savedData) {
        console.log(`[NewExcel:loadNonMemberData] 데이터 발견 - 길이: ${savedData.length}, 데이터:`, savedData.substring(0, 100) + '...');
        try {
          const parsedData = JSON.parse(savedData) as CustomerRow[];
          
          // 데이터 유효성 검사 및 정제
          const cleanedData = parsedData.map((row, index) => {
            // 닉네임 필드에 스토리지 키가 들어가는 경우 처리
            const nickname = row.nickname?.includes(`parsed_order_${currentPseudoUserId}`) ? "" : row.nickname;
            
            return {
              ...row,
              nickname, // nickname은 위에서 이미 한번 처리되었을 수 있으나, 안전하게 한번 더
              name: isEmptyFieldValue(row.name) ? "" : row.name,
              phone: isEmptyFieldValue(row.phone) ? "" : row.phone,
              address: isEmptyFieldValue(row.address) ? "" : row.address,
              deliveryNotes: isEmptyFieldValue(row.deliveryNotes || "") ? "" : (row.deliveryNotes || ""),
              productName: isEmptyFieldValue(row.productName || "") ? "" : (row.productName || ""),
              row_order: index // row_order 재설정
            };
          });
          
          console.log(`[NewExcel:loadNonMemberData] 처리된 데이터 - ${cleanedData.length}개 행`);
          cleanedData.forEach((row, i) => {
            console.log(`행 ${i+1}: id=${row.id}, nickname="${row.nickname}", name="${row.name}", productName="${row.productName}"`);
          });
          
          setRows(cleanedData);
          setShowEmptyState(cleanedData.length === 0);
          
          // 데이터가 정제되었으면 다시 저장
          if (JSON.stringify(cleanedData) !== savedData) {
            console.log(`[NewExcel:loadNonMemberData] 정제된 데이터 재저장`);
            sessionStorage.setItem(storageKey, JSON.stringify(cleanedData));
          }
        } catch (parseError) {
          console.error(`[NewExcel:loadNonMemberData] JSON 파싱 오류:`, parseError);
          setRows([]);
          setShowEmptyState(true);
        }
      } else {
        console.log(`[NewExcel:loadNonMemberData] 해당 날짜의 데이터 없음`);
        setRows([]);
        setShowEmptyState(true);
      }
      
      // 하이라이트 날짜 로드 (데이터가 있는 날짜들)
      loadNonMemberHighlightedDates();
    } catch (err) {
      console.error("[NewExcel:loadNonMemberData] 데이터 로드 중 오류:", err);
      setError("데이터를 불러오는 중 오류가 발생했습니다.");
      setRows([]);
      setShowEmptyState(true);
    } finally {
      setIsLoading(false);
    }
  }

  // 비회원 사용자의 데이터가 있는 날짜 목록 로드
  const loadNonMemberHighlightedDates = () => {
    try {
      // 항상 최신 pseudoUserId 사용
      const currentPseudoUserId = getPseudoUserId();
      
      const storageKey = `highlighted_dates_${currentPseudoUserId}`;
      console.log(`[NewExcel:loadNonMemberHighlightedDates] 날짜 목록 로드 시도, 키: ${storageKey}`);
      
      const savedDates = sessionStorage.getItem(storageKey);
      
      if (savedDates) {
        try {
          console.log(`[NewExcel:loadNonMemberHighlightedDates] 저장된 날짜 목록 발견: ${savedDates}`);
          const parsedDates = JSON.parse(savedDates) as string[];
          
          // 문자열 날짜를 Date 객체로 변환
          const dates = parsedDates.map(dateStr => {
            try {
              return new Date(dateStr);
            } catch (e) {
              console.error(`[NewExcel:loadNonMemberHighlightedDates] 날짜 변환 오류: ${dateStr}`, e);
              return null;
            }
          }).filter(d => d !== null) as Date[];
          
          console.log(`[NewExcel:loadNonMemberHighlightedDates] 날짜 변환 완료 - ${dates.length}개 날짜`);
          setHighlightedDates(dates);
        } catch (e) {
          console.error(`[NewExcel:loadNonMemberHighlightedDates] JSON 파싱 오류:`, e);
          setHighlightedDates([]);
        }
      } else {
        console.log(`[NewExcel:loadNonMemberHighlightedDates] 저장된 날짜 목록 없음`);
        setHighlightedDates([]);
      }
    } catch (err) {
      console.error("[NewExcel:loadNonMemberHighlightedDates] 날짜 목록 로드 중 오류:", err);
      setHighlightedDates([]);
    }
  }

  // 데이터가 있는 날짜 목록 불러오기
  const fetchDatesWithData = async (uid: string) => {
    try {
      const { data, error } = await supabase
        .from("parsed_order")
        .select("order_date")
        .eq("user_id", uid)
        .order("order_date", { ascending: false })

      if (error) throw error

      if (data && data.length > 0) {
        // 중복 날짜 제거
        const uniqueDates = [...new Set(data.map(item => item.order_date))]
        // Date 객체로 변환
        const dates = uniqueDates.map(dateStr => new Date(dateStr))
        setHighlightedDates(dates)
      } else {
        setHighlightedDates([])
      }
    } catch (err: any) {
      logError("Error fetching dates with data:", err)
    }
  }

  // 이전 날짜로 이동
  const handlePrevDay = () => {
    setSelectedDate(prev => subDays(prev, 1))
  }

  // 다음 날짜로 이동
  const handleNextDay = () => {
    setSelectedDate(prev => addDays(prev, 1))
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomerText(e.target.value)
    setError(null)
  }

  // TODO: 임시 핫픽스 — 해당 유저 요청 해소 시 제거
  const NICKNAME_MERGE_USER_ID = '9d62b24c-2489-403a-9a0a-a234f56a0440';

  const parseCustomerText = async () => {
    if (!user) {
      // 비회원 사용자인 경우 사용 제한 체크
      const currentPseudoUserId = getPseudoUserId();
      const { canUse } = checkNonMemberUsage(currentPseudoUserId);
      
      if (!canUse) {
        setShowNonMemberLimitDialog(true);
        return;
      }
    }

    setIsProcessing(true);
    try {
      if (!customerText.trim()) {
        setError("고객 메시지를 먼저 붙여넣으세요")
        return
      }

      if (customerText.trim().length < 20) {
        setError("입력값이 너무 짧습니다. 최소 20자 이상 입력해주세요.")
        return
      }

      const meaninglessPattern = /^[ㅋㅎㅁㄴㄷㄱ\s,.!?~]+$/
      if (meaninglessPattern.test(customerText.trim())) {
        setError("의미 없는 입력은 처리할 수 없습니다.")
        return
      }

      // 2. 텍스트 파싱 처리
      const res = await fetch("https://gsvtnnoutinzsbasjvoz.supabase.co/functions/v1/parse-reply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdzdnRubm91dGluenNiYXNqdm96Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAwNjA3ODMsImV4cCI6MjA1NTYzNjc4M30.nc2HXlY8_dKhPbBbem6nCmaUv_cDe8jsuSh-D13ijhI`
        },
        // 닉네임/이름 합치기 유저: 서버에는 parseNickname: true로 보내 분리 반환받고, 클라이언트에서 후처리로 합침
        body: JSON.stringify({ input: customerText, options: user?.id === NICKNAME_MERGE_USER_ID ? { ...parsingOptions, parseNickname: true } : parsingOptions })
      })

      const data = await res.json()

      if (!res.ok || !data.output) {
        throw new Error(data.error || "파싱에 실패했습니다.")
      }

      // 비회원인 경우 사용량 증가
      if (!user) {
        const currentPseudoUserId = getPseudoUserId();
        incrementNonMemberUsage(currentPseudoUserId);
      } else {
        // 1. 회원인 경우 사용량 체크 및 증가
        const usageResult = await paymentService.incrementUsage(user.id);
        if (!usageResult.success) {
          if (usageResult.reason === 'usage_limit_exceeded') {
            setShowUsageLimitDialog(true);
            setIsProcessing(false); // 처리 중 상태 해제
            return; // 여기서 함수 실행 중단
          } else {
            // 다른 이유로 실패한 경우 (예: 구독 정보 없음 등)
            logError('사용량 증가 실패:', new Error(usageResult.reason));
            // 사용자가 구독 정보가 없거나 할 때 에러 메시지를 그대로 노출하기 보다 안내 메시지로 변경
            if (usageResult.reason === 'no_active_subscription' || usageResult.reason === 'no_usage_stat' || usageResult.reason === 'no_plan_info') {
              setError("구독 정보를 확인해주세요. 문제가 지속되면 고객센터로 문의해주세요.");
            } else {
              setError(usageResult.reason || "사용량 처리 중 오류가 발생했습니다.");
            }
            setIsProcessing(false); // 처리 중 상태 해제
            return; // 여기서 함수 실행 중단
          }
        }
      }

      // 특정 유저: 닉네임/이름 합치기
      if (user?.id === NICKNAME_MERGE_USER_ID && !isEmptyFieldValue(data.output.nickname)) {
        const name = isEmptyFieldValue(data.output.name) ? '' : data.output.name;
        data.output.name = name ? `${data.output.nickname}/${name}` : data.output.nickname;
        data.output.nickname = '';
      }

      const phone = formatPhoneNumber(data.output.phone);

      if (currentUserIsAuthenticated && currentUserId) {
        // 로그인 되어 있으면 Supabase에 저장
        try {
          // 선택된 날짜 형식으로 변환
          const formattedDate = format(selectedDate, "yyyy-MM-dd");

          // row_order 계산: 마지막+1 (rows.length는 날짜 이동으로 순번이 겹친 날짜에서 기존 값과 충돌)
          const rowOrder = rows.reduce((max, r) => typeof r.row_order === "number" ? Math.max(max, r.row_order) : max, -1) + 1;

          // Supabase에 추가
          const { data: inserted, error: insertError } = await supabase
            .from("parsed_order")
            .insert({
              user_id: currentUserId,
              order_date: formattedDate,
              row_order: rowOrder,
              nickname: isEmptyFieldValue(data.output.nickname) ? "" : data.output.nickname,
              name: isEmptyFieldValue(data.output.name) ? "" : data.output.name,
              phone_number: isEmptyFieldValue(phone) ? "" : phone,
              address: isEmptyFieldValue(data.output.address) ? "" : data.output.address,
              delivery_notes: isEmptyFieldValue(data.output.deliveryNotes) ? "" : data.output.deliveryNotes,
              product_name: isEmptyFieldValue(data.output.productName) ? "" : data.output.productName
            })
            .select()
            .single();

          if (insertError) {
            console.error(`[NewExcel:parseCustomerText] DB 저장 실패:`, insertError);
            throw insertError;
          }
          
          // 새 데이터 포맷팅
          const newRow: CustomerRow = {
            id: Number(inserted.id),
            nickname: isEmptyFieldValue(inserted.nickname) ? "" : inserted.nickname,
            name: isEmptyFieldValue(inserted.name) ? "" : inserted.name,
            phone: isEmptyFieldValue(inserted.phone_number) ? "" : inserted.phone_number,
            address: isEmptyFieldValue(inserted.address) ? "" : inserted.address,
            deliveryNotes: isEmptyFieldValue(inserted.delivery_notes) ? "" : inserted.delivery_notes,
            productName: isEmptyFieldValue(inserted.product_name) ? "" : inserted.product_name,
            row_order: inserted.row_order
          };

          setRows((prevRows) => [...prevRows, newRow]);

          // 데이터가 있는 날짜 다시 불러오기
          if (!highlightedDates.some(date => isSameDay(date, selectedDate))) {
            fetchDatesWithData(currentUserId);
          }
        } catch (err: any) {
          logError("Error saving to database:", err);
          setError("데이터베이스 저장 중 오류가 발생했습니다.");
        }
      } else {
        // 비회원인 경우 sessionStorage에 저장
        console.log(`[NewExcel:parseCustomerText] 비회원 데이터 저장 시작 - output:`, data.output);
        
        const currentPseudoUserId = getPseudoUserId();
        
        // 닉네임 필드 유효성 검사
        let nickname = data.output.nickname || "";
        if (nickname.includes(`parsed_order_${currentPseudoUserId}`)) {
          nickname = "";
        }
        
        const newRow: CustomerRow = {
          id: Date.now(),
          nickname: isEmptyFieldValue(nickname) ? "" : nickname, // nickname은 위에서 이미 한번 처리되었을 수 있으나, 안전하게 한번 더
          name: isEmptyFieldValue(data.output.name) ? "" : data.output.name,
          phone: isEmptyFieldValue(phone) ? "" : phone,
          address: isEmptyFieldValue(data.output.address) ? "" : data.output.address,
          deliveryNotes: isEmptyFieldValue(data.output.deliveryNotes || "") ? "" : (data.output.deliveryNotes || ""),
          productName: isEmptyFieldValue(data.output.productName || "") ? "" : (data.output.productName || ""),
          row_order: rows.length
        };
        
        // 상태 업데이트 최적화
        setShowEmptyState(false);  // 1. 먼저 빈 상태 해제
        
        // 새 배열 생성 및 설정
        const updatedRows = [...rows, newRow];
        setRows(updatedRows);      // 2. 실제 데이터 업데이트
        
        // 3. 저장소에 저장
        saveNonMemberData(updatedRows, selectedDate);
        
        // 첫 번째 항목 추가 시 추가 처리
        if (rows.length === 0) {
          console.log('[NewExcel:parseCustomerText] 첫 항목 추가 - 렌더링 강제 업데이트');
          // 렌더링 사이클에 맞춰 UI 업데이트 최종 확인
          requestAnimationFrame(() => {
            setShowEmptyState(false);
          });
        }
      }

      // 인덱스 설정 (하이라이트 표시용)
      setNewRowIndex(rows.length);
      setCustomerText("");
      
      // 빈 상태 확실히 제거
      if (showEmptyState) {
        setShowEmptyState(false);
      }

      // 로그 기록 함수를 비동기적으로 실행하여 메인 로직 흐름에 영향을 주지 않도록 함
      setTimeout(() => {
        // 사용 로그 기록 (logUsage 함수 사용)
        const currentPseudoUserId = getPseudoUserId();
        logUsage({
          userId: currentUserId || null,
          pseudoUserId: currentPseudoUserId,
          isMember: !!currentUserId,
          type: 'parse'
        }).catch(err => {
          logError("사용 로그 기록 중 오류 발생:", err);
        });
      }, 0);

      // 새 행 추가 후 해당 행으로 스크롤
      setTimeout(() => {
        const lastRow = document.querySelector('tbody tr:last-child')
        if (lastRow) {
          lastRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        setTimeout(() => setNewRowIndex(null), 3000)
      }, 100)
    } catch (error: any) {
      logError('텍스트 정리 중 오류 발생:', error);
      setError(error.message || "처리 중 오류가 발생했습니다");
    } finally {
      setIsProcessing(false)
    }
  }

  const deleteRow = async (id: number) => {
    try {
      // 삭제할 행의 row_order 값을 찾습니다
      const rowToDelete = rows.find(row => row.id === id);
      if (!rowToDelete) return;

      const deletedRowOrder = rowToDelete.row_order || 0;
      const formattedDate = format(selectedDate, "yyyy-MM-dd"); // 선택된 날짜 사용

      // 삭제가 커밋되면 뒤 행들의 row_order 재인덱스 UPDATE가 modified_at을 올린다 —
      // 재인덱스가 부분 실패해도 후보 기준값은 이미 변했으므로 "삭제 커밋" 기준으로 재스캔.
      let dbDeleteCommitted = false;

      // 로그인 되어 있으면 Supabase에서도 삭제
      if (currentUserIsAuthenticated && currentUserId) {
        try {
          console.log(`[NewExcel:deleteRow] 삭제 시도 - id: ${id}, date: ${formattedDate}`);
          
          // 1. 삭제할 행 제거
          const { error: deleteError } = await supabase
            .from("parsed_order")
            .delete()
            .eq("id", id);

          if (deleteError) {
            console.error(`[NewExcel:deleteRow] 삭제 실패:`, deleteError);
            throw deleteError;
          }
          
          dbDeleteCommitted = true;
          console.log(`[NewExcel:deleteRow] 삭제 성공 - id: ${id}`);

          // 2. 이후 행들의 row_order 업데이트
          const { data: rowsToUpdate, error: fetchError } = await supabase
            .from("parsed_order")
            .select("id, row_order")
            .eq("user_id", currentUserId)
            .eq("order_date", formattedDate)
            .gt("row_order", deletedRowOrder);

          if (fetchError) {
            console.error(`[NewExcel:deleteRow] row_order 업데이트를 위한 데이터 조회 실패:`, fetchError);
            throw fetchError;
          }

          // 각 행의 row_order를 1씩 감소시킵니다
          console.log(`[NewExcel:deleteRow] row_order 업데이트 대상: ${rowsToUpdate?.length || 0}건`);
          if (rowsToUpdate && rowsToUpdate.length > 0) {
            for (const row of rowsToUpdate) {
              await supabase
                .from("parsed_order")
                .update({ row_order: row.row_order - 1 })
                .eq("id", row.id);
            }
          }
        } catch (err: any) {
          logError("Error deleting from database:", err);
          toast.error("삭제 중 오류가 발생했습니다.");
        }
      } else {
        // 비회원인 경우 sessionStorage에서 삭제 및 업데이트
        const updatedRows = rows
          .filter(row => row.id !== id)
          .map(row => {
            if (row.row_order && row.row_order > deletedRowOrder) {
              return { ...row, row_order: row.row_order - 1 };
            }
            return row;
          });
          
        // sessionStorage 업데이트
        saveNonMemberData(updatedRows, selectedDate);
      }

      // 삭제 커밋 시 stale 재스캔 트리거 — 반영 중 도착하면 훅의 지연 큐가 안전하게 소화한다.
      if (dbDeleteCommitted) {
        setStaleSyncRefreshToken((prev) => prev + 1);
      }

      // UI에서 삭제 및 row_order 업데이트 (로그인 여부와 상관없이)
      setRows(prevRows => {
        const updatedRows = prevRows
          .filter(row => row.id !== id)
          .map(row => {
            if (row.row_order && row.row_order > deletedRowOrder) {
              return { ...row, row_order: row.row_order - 1 };
            }
            return row;
          });

        return updatedRows;
      });

      // 단건 삭제 시 선택 상태에서도 제거
      setSelectedRowIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })

      if (rows.length === 1) {
        setShowEmptyState(true);
      }
    } catch (err: any) {
      logError("Error deleting row:", err);
      toast.error("삭제 중 오류가 발생했습니다.");
    }
  }

  // 일괄 선택/삭제 핸들러
  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedRowIds(new Set(rows.map(r => r.id)))
    } else {
      setSelectedRowIds(new Set())
    }
  }

  const handleSelectRow = (id: number, checked: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      checked ? next.add(id) : next.delete(id)
      return next
    })
  }

  const allSelected = rows.length > 0 && selectedRowIds.size === rows.length
  const someSelected = selectedRowIds.size > 0 && selectedRowIds.size < rows.length
  const headerCheckState: boolean | "indeterminate" = allSelected ? true : someSelected ? "indeterminate" : false

  const handleBulkDelete = async () => {
    if (selectedRowIds.size === 0) return
    setIsBulkDeleting(true)
    const idsToDelete = Array.from(selectedRowIds)
    const deleteSet = new Set(idsToDelete)
    const formattedDate = format(selectedDate, "yyyy-MM-dd")

    try {
      if (currentUserIsAuthenticated && currentUserId) {
        const { error } = await supabase
          .from("parsed_order")
          .delete()
          .eq("user_id", currentUserId)
          .eq("order_date", formattedDate)
          .in("id", idsToDelete)
        if (error) throw error

        const { data: remaining, error: fetchError } = await supabase
          .from("parsed_order")
          .select("id, row_order")
          .eq("user_id", currentUserId)
          .eq("order_date", formattedDate)
          .order("row_order", { ascending: true })
          .order("created_at", { ascending: true })

        if (fetchError) {
          logError("Error fetching remaining rows for reindex:", fetchError)
        } else if (remaining) {
          for (let i = 0; i < remaining.length; i++) {
            if (remaining[i].row_order !== i) {
              const { error: updateError } = await supabase
                .from("parsed_order")
                .update({ row_order: i })
                .eq("id", remaining[i].id)
              if (updateError) {
                logError(`Error updating row_order for id ${remaining[i].id}:`, updateError)
              }
            }
          }
        }

        fetchDatesWithData(currentUserId)

        // 일괄 삭제도 재인덱스 UPDATE로 잔여 행 modified_at이 변한다 — 삭제 성공 경로에서
        // 1회 재스캔(재인덱스 부분 실패는 로그만 남고 여기 도달 = bump가 맞다, 구현 델타 합의).
        setStaleSyncRefreshToken((prev) => prev + 1)
      } else {
        const updatedRows = rows
          .filter(r => !deleteSet.has(r.id))
          .map((r, i) => ({ ...r, row_order: i }))
        if (updatedRows.length === 0) {
          const pseudoId = getPseudoUserId()
          sessionStorage.removeItem(`parsed_order_${pseudoId}_${formattedDate}`)
          const hlKey = `highlighted_dates_${pseudoId}`
          const savedDates = sessionStorage.getItem(hlKey)
          if (savedDates) {
            try {
              const dates = JSON.parse(savedDates) as string[]
              const filtered = dates.filter(d => d !== formattedDate)
              sessionStorage.setItem(hlKey, JSON.stringify(filtered))
              setHighlightedDates(filtered.map(d => new Date(d)))
            } catch (e) {
              sessionStorage.removeItem(hlKey)
              setHighlightedDates([])
            }
          }
        } else {
          saveNonMemberData(updatedRows, selectedDate)
        }
      }

      const deletedCount = idsToDelete.length
      const remainingRows = rows.filter(r => !deleteSet.has(r.id))
      setRows(remainingRows.map((r, i) => ({ ...r, row_order: i })))
      setSelectedRowIds(new Set())
      setShowBulkDeleteDialog(false)
      if (remainingRows.length === 0) setShowEmptyState(true)
      toast.success(`${deletedCount}건이 삭제되었습니다.`)
    } catch (err: any) {
      logError("Error during bulk delete:", err)
      toast.error("일괄 삭제 중 오류가 발생했습니다.")
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const handleEditClick = (row: CustomerRow, field: keyof CustomerRow) => {
    console.log(`[NewExcel:handleEditClick] 편집 시작 - id: ${row.id}, field: ${field}`);
    setEditingRowId(row.id);

    // id를 제외한 필드만 편집 가능하도록 타입 체크
    if (field !== 'id') {
      setEditingField(field as 'name' | 'nickname' | 'phone' | 'address' | 'deliveryNotes' | 'productName'); // productName 추가
    }

    // 값이 "string"인 경우 빈 문자열로 초기화
    const updatedRow = { ...row };
    if (typeof row[field] === 'string') {
      const fieldValue = row[field] as string;
      if (isEmptyFieldValue(fieldValue)) {
        (updatedRow[field] as unknown) = "";
      }
    }
    setEditFormData(updatedRow);
  }

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof CustomerRow,
  ) => {
    if (editFormData) {
      setEditFormData({
        ...editFormData,
        [field]: e.target.value,
      })
    }
  }

  const handleSaveClick = async () => {
    if (editFormData && editingRowId && editingField) {
      console.log(`[NewExcel:handleSaveClick] 저장 시도 - id: ${editingRowId}, field: ${editingField}, value: ${editFormData[editingField]}`);
      
      // 저장 전, 현재 편집 중인 필드 값에 대해 isEmptyFieldValue 적용
      const valueToSave = isEmptyFieldValue(String(editFormData[editingField])) ? "" : editFormData[editingField];
      const cleanedEditFormData = { ...editFormData, [editingField]: valueToSave };

      // DB 저장이 실제로 커밋된 경우에만 stale 재스캔을 트리거한다(실패 시 bump 금지).
      let dbSaved = false;

      // 로그인되어 있으면 Supabase에도 업데이트
      if (currentUserIsAuthenticated && currentUserId) {
        try {
          // DB 필드명으로 변환
          const fieldMapping: Record<string, string> = {
            name: 'name',
            nickname: 'nickname',
            phone: 'phone_number',
            address: 'address',
            deliveryNotes: 'delivery_notes',
            productName: 'product_name'
          };

          // Supabase 업데이트
          const { error } = await supabase
            .from("parsed_order")
            .update({
              [fieldMapping[editingField]]: valueToSave // 정제된 값으로 업데이트
            })
            .eq("id", editingRowId);

          if (error) {
            console.error(`[NewExcel:handleSaveClick] 업데이트 실패:`, error);
            throw error;
          }

          dbSaved = true;
          console.log(`[NewExcel:handleSaveClick] DB 업데이트 성공 - id: ${editingRowId}`);
        } catch (err: any) {
          logError("Error updating field:", err);
          toast.error(`${editingField} 저장 중 오류 발생`);
        }
      } else {
        // 비회원인 경우 sessionStorage 업데이트
        const updatedRows = rows.map((row) => (row.id === editingRowId ? cleanedEditFormData : row));
        saveNonMemberData(updatedRows, selectedDate);
        console.log(`[NewExcel:handleSaveClick] sessionStorage 업데이트 완료`);
      }

      // UI 업데이트 (로그인 여부와 상관없이)
      // 함수형 업데이트 + 편집 필드만 패치: DB에는 editingField 하나만 쓰면서 로컬은
      // 편집 시작 시점 스냅샷(cleanedEditFormData)으로 행 전체를 교체하면, 이 저장이 느릴 때
      // 그 사이 도착한 다른 필드의 갱신(stale 일괄 반영의 productName 패치 등)을 되돌린다.
      setRows((prev) =>
        prev.map((row) =>
          row.id === editingRowId ? { ...row, [editingField]: valueToSave } : row
        )
      );
      console.log(`[NewExcel:handleSaveClick] UI 업데이트 완료`);

      // 어떤 필드든 성공한 인라인 저장은 BEFORE UPDATE 트리거로 modified_at을 올려
      // 후보의 CAS 기준값(expectedModifiedAt)을 낡게 만든다 — 상품명만이 아니라 전 필드에서
      // 재스캔한다(구현 델타 합의: 미재스캔 시 반영에서 대량 spurious conflict).
      if (dbSaved) {
        setStaleSyncRefreshToken((prev) => prev + 1);
      }
    }

    setEditingRowId(null);
    setEditingField(null);
    setEditFormData(null);
  }

  const handleCancelClick = () => {
    setEditingRowId(null)
    setEditingField(null)
    setEditFormData(null)
  }

  // 채워야 하는 필드인 경우 표시할 메시지
  const renderEmptyFieldValue = (value: string, field: keyof CustomerRow) => {
    // 배송메모는 빈 값, 문자열 null 등이어도 메시지 표시하지 않음
    if (field === "deliveryNotes") {
      const lower = String(value).toLowerCase().trim();
      if (["", "string", "null", "undefined", "없음"].includes(lower)) {
        return "";
      }
      return value;
    }

    if (isEmptyFieldValue(value)) {
      return <span className="text-gray-400 italic">값을 입력해주세요</span>
    }
    return value
  }

  // 컬럼 리사이즈 시작
  const handleResizeStart = (e: React.MouseEvent, column: keyof ColumnWidth) => {
    e.preventDefault()
    setResizingColumn(column)
    setStartX(e.clientX)
    setStartWidth(columnWidths[column])
  }

  // 컬럼 리사이즈 중
  const handleResizeMove = (e: MouseEvent) => {
    if (!resizingColumn) return

    const tableWidth = tableRef.current?.offsetWidth || 1200
    const delta = e.clientX - startX
    const percentDelta = (delta / tableWidth) * 100

    // 관리 컬럼은 항상 고정 크기로 유지
    // 현재 보이는 드래그 가능한 컬럼들 (actions 제외)
    const 현재보이는드래그가능컬럼들 = (['orderNum', 'nickname', 'name', 'phone', 'address', 'productName', 'deliveryNotes'] as (keyof ColumnWidth)[])
      .filter(key => {
        if (key === 'nickname' && !parsingOptions.parseNickname) return false;
        if (key === 'productName' && !parsingOptions.parseProductName) return false;
        return true;
      });

    if (!resizingColumn || !현재보이는드래그가능컬럼들.includes(resizingColumn)) {
      if (resizingColumn && !현재보이는드래그가능컬럼들.includes(resizingColumn)) {
        setResizingColumn(null); // 숨겨진 컬럼의 리사이저를 드래그 시도한 경우
      }
      return;
    }

    const 임시너비 = { ...columnWidths };
    const 새리사이징컬럼너비 = Math.max(5, Math.min(50, startWidth + percentDelta));
    const 너비변화량 = 새리사이징컬럼너비 - startWidth;

    임시너비[resizingColumn] = 새리사이징컬럼너비;

    const 다른보이는컬럼들 = 현재보이는드래그가능컬럼들.filter(col => col !== resizingColumn);
    const 다른보이는컬럼들의현재너비합 = 다른보이는컬럼들.reduce((sum, col) => sum + columnWidths[col], 0);

    if (다른보이는컬럼들.length > 0 && 다른보이는컬럼들의현재너비합 > 0) {
      // 다른 컬럼들이 최소 너비(5%)를 유지하도록 변화량 조정
      let 실제분배될너비변화량 = -너비변화량; // 다른 컬럼들 입장에서는 반대로 적용됨

      // 다른 컬럼들의 총 너비가 (최소너비 * 개수) 보다 작아지지 않도록
      if (다른보이는컬럼들의현재너비합 + 실제분배될너비변화량 < 다른보이는컬럼들.length * 5) {
        실제분배될너비변화량 = 다른보이는컬럼들.length * 5 - 다른보이는컬럼들의현재너비합;
        임시너비[resizingColumn] = startWidth - 실제분배될너비변화량; // 리사이징 컬럼 너비도 이에 맞춰 재조정
      }
      
      다른보이는컬럼들.forEach(col => {
        const 가중치 = columnWidths[col] / 다른보이는컬럼들의현재너비합;
        임시너비[col] = Math.max(5, columnWidths[col] + (실제분배될너비변화량 * 가중치));
      });
    }

    // 모든 '보이는' 드래그 가능 컬럼 너비의 합이 (100 - actions 너비)가 되도록 최종 보정
    const 최종보이는컬럼들 = (['orderNum', 'nickname', 'name', 'phone', 'address', 'productName', 'deliveryNotes'] as (keyof ColumnWidth)[])
    .filter(key => {
      if (key === 'nickname' && !parsingOptions.parseNickname) return false;
      if (key === 'productName' && !parsingOptions.parseProductName) return false;
      return true;
    });

    let 현재총너비 = 최종보이는컬럼들.reduce((sum, col) => sum + 임시너비[col], 0);
    const 목표총너비 = 100 - 임시너비.actions - 임시너비.checkbox;

    if (Math.abs(현재총너비 - 목표총너비) > 0.1 && 현재총너비 > 0) {
      const 보정계수 = 목표총너비 / 현재총너비;
      최종보이는컬럼들.forEach(col => {
        임시너비[col] *= 보정계수;
      });
    }
    setColumnWidths(임시너비);
  }

  // 컬럼 리사이즈 종료
  const handleResizeEnd = () => {
    setResizingColumn(null)
  }

  // 마우스 이벤트 리스너 설정
  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
    } else {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }
  }, [resizingColumn, startX, startWidth, columnWidths])

  // 커서를 맨 오른쪽으로 이동시키는 useEffect
  useEffect(() => {
    if (editingRowId && editingField && inputRefs[editingField]?.current) {
      const input = inputRefs[editingField].current;
      if (input) {
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    }
  }, [editingRowId, editingField]);

  // 엑셀 파일 다운로드 함수
  const handleExcelDownload = async () => {
    if (displayRows.length === 0) {
      setError("고객 정보를 추가해보세요!")

      // Alert 메시지 표시
      window.alert("아직 추가된 고객 정보가 없습니다. 고객 정보를 추가한 후 다시 시도해주세요.")
      return
    }

    try {
      await downloadExcel(displayRows, parsingOptions) // parsingOptions 전달

      // 사용 로그 기록
      await logUsage({
        userId: currentUserId || null,
        pseudoUserId: getPseudoUserId(),
        isMember: !!currentUserId,
        type: 'excel'
      }).catch(err => {
        logError("사용 로그 기록 중 오류 발생:", err);
      });
    } catch (error) {
      logError("Excel download error:", error)
      toast.error("엑셀 파일 다운로드 중 오류가 발생했습니다.")
    }
  }

  // 선택된 날짜 기준 최근 4주 킵만 엑셀 다운로드 (template가 전달되면 템플릿 양식으로 다운로드)
  const handleKeepExcelDownload4Weeks = async (template?: DeliveryTemplate | null) => {
    if (!currentUserIsAuthenticated || !currentUserId) {
      toast.info('로그인 후 이용 가능한 기능입니다.');
      openLoginModal();
      return;
    }

    try {
      setIsKeepDownloading(true);

      // 기간 계산: 선택된 날짜 포함 최근 28일 (selectedDate-27 ~ selectedDate)
      // 과거엔 라벨("선택된 날짜부터")과 달리 오늘(new Date()) 앵커였음 — 라벨 실동작 일치로 수정 (고객 문의 발, 2026-07-11)
      const anchor = selectedDate;
      const start = subDays(anchor, 27);
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(anchor, 'yyyy-MM-dd');

      // 1) 최근 4주 킵 주문 조회
      const { data: keptOrders, error: keptErr } = await supabase
        .from('order')
        .select('id, order_date')
        .eq('user_id', currentUserId)
        .eq('is_keep', true)
        .gte('order_date', startStr)
        .lte('order_date', endStr);

      if (keptErr) throw keptErr;

      const keptOrderIds = (keptOrders || []).map(o => o.id);
      if (!keptOrderIds.length) {
        toast.info('선택된 날짜 기준 최근 4주 킵 주문이 없습니다.');
        return;
      }

      const orderIdToDate = new Map<number, string>();
      (keptOrders || []).forEach(o => { orderIdToDate.set(o.id, o.order_date as unknown as string); });

      // 2) parsed_order에서 order_id 로 매칭된 행 수집 (청크 처리)
      const chunkSize = 500;
      const chunks: number[][] = [];
      for (let i = 0; i < keptOrderIds.length; i += chunkSize) {
        chunks.push(keptOrderIds.slice(i, i + chunkSize));
      }

      let collected: any[] = [];
      for (const ids of chunks) {
        const { data, error } = await supabase
          .from('parsed_order')
          // 정렬 안정화를 위해 created_at도 함께 가져옵니다.
          .select('id, nickname, name, phone_number, address, delivery_notes, product_name, row_order, order_id, created_at')
          .eq('user_id', currentUserId)
          .in('order_id', ids);
        if (error) throw error;
        if (data) collected = collected.concat(data);
      }

      if (!collected.length) {
        toast.info('매칭된 배송엑셀 데이터가 없습니다.');
        return;
      }

      // 3) 정렬: order_date ASC -> row_order ASC -> created_at ASC -> id ASC
      // - row_order가 전부 0인(또는 동률인) 날짜에서도 created_at/id로 안정적인 순서를 보장합니다.
      collected.sort((a, b) => {
        const da = orderIdToDate.get(a.order_id) || '';
        const db = orderIdToDate.get(b.order_id) || '';
        if (da < db) return -1;
        if (da > db) return 1;
        // 정산서 순서: 같은 날짜 안에서 order_id ASC (킵 행은 전부 order_id 보유)
        if (effectiveShippingSort === 'settlement' && a.order_id !== b.order_id) return a.order_id - b.order_id;
        const ra = typeof a.row_order === 'number' ? a.row_order : null;
        const rb = typeof b.row_order === 'number' ? b.row_order : null;
        if (ra !== null && rb !== null) {
          if (ra !== rb) return ra - rb;
        } else if (ra !== null) {
          // row_order가 있는 레코드를 우선 (환경에 따라 row_order가 nullable일 수 있음)
          return -1;
        } else if (rb !== null) {
          return 1;
        }

        const ca = typeof a.created_at === 'string' ? a.created_at : '';
        const cb = typeof b.created_at === 'string' ? b.created_at : '';
        if (ca !== cb) return ca.localeCompare(cb);

        const ida = typeof a.id === 'number' ? a.id : 0;
        const idb = typeof b.id === 'number' ? b.id : 0;
        return ida - idb;
      });

      // 4) CustomerRow로 매핑
      const mappedRows: CustomerRow[] = collected.map(item => ({
        id: Number(item.id),
        name: isEmptyFieldValue(item.name) ? '' : item.name,
        nickname: isEmptyFieldValue(item.nickname) ? '' : item.nickname,
        phone: isEmptyFieldValue(item.phone_number) ? '' : item.phone_number,
        address: isEmptyFieldValue(item.address) ? '' : item.address,
        deliveryNotes: isEmptyFieldValue(item.delivery_notes || '') ? '' : (item.delivery_notes || ''),
        productName: isEmptyFieldValue(item.product_name || '') ? '' : (item.product_name || ''),
        // row_order를 "없으면 0"으로 뭉개지 않고, 있는 경우에만 유지합니다.
        row_order: (typeof item.row_order === 'number' ? item.row_order : undefined),
      }));

      if (template) {
        await downloadExcelWithTemplate(mappedRows, template);
      } else {
        await downloadExcel(mappedRows, parsingOptions);
      }

      await logUsage({
        userId: currentUserId || null,
        pseudoUserId: getPseudoUserId(),
        isMember: !!currentUserId,
        type: template ? 'excel_template' : 'excel'
      }).catch(err => {
        logError("사용 로그 기록 중 오류 발생:", err);
      });

      const keptCount = keptOrderIds.length;
      const exportedCount = mappedRows.length;
      const excluded = Math.max(0, keptCount - exportedCount);
      toast.success(`선택된 날짜 기준 최근 4주 킵 주문 ${keptCount}건 중 ${exportedCount}건을 다운로드했어요.`);
      if (excluded > 0) {
        toast.info(`배송 엑셀 데이터가 아직 없는 주문 ${excluded}건은 제외되었어요.`);
      }
    } catch (e) {
      logError('Keep-only Excel download error:', e as any);
      toast.error('킵 목록 엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsKeepDownloading(false);
    }
  }

  const handleTemplateExcelDownload = async () => {
    if (!savedTemplate) return;

    if (downloadMode === 'keep4w') {
      await handleKeepExcelDownload4Weeks(savedTemplate);
      return;
    }

    if (downloadMode === 'all_exclude_keep') {
      await handleExcludeKeepExcelDownload(savedTemplate);
      return;
    }

    if (displayRows.length === 0) {
      setError("고객 정보를 추가해보세요!")
      window.alert("아직 추가된 고객 정보가 없습니다. 고객 정보를 추가한 후 다시 시도해주세요.")
      return
    }

    try {
      await downloadExcelWithTemplate(displayRows, savedTemplate)

      await logUsage({
        userId: currentUserId || null,
        pseudoUserId: getPseudoUserId(),
        isMember: !!currentUserId,
        type: 'excel_template'
      }).catch(err => {
        logError("사용 로그 기록 중 오류 발생:", err);
      });
    } catch (error) {
      logError("Template Excel download error:", error)
      toast.error("엑셀 파일 다운로드 중 오류가 발생했습니다.")
    }
  }

  const handleUnifiedExcelDownload = async () => {
    if (downloadMode === 'keep4w') {
      await handleKeepExcelDownload4Weeks();
    } else if (downloadMode === 'all_exclude_keep') {
      await handleExcludeKeepExcelDownload();
    } else {
      await handleExcelDownload();
    }
  }

  const handleDownloadModeChange = (value: string) => {
    if (!value) return;
    if ((value === 'keep4w' || value === 'all_exclude_keep') && !currentUserIsAuthenticated) {
      toast.info('로그인 후 이용 가능한 기능입니다.');
      openLoginModal();
      return;
    }
    const next = (value === 'keep4w' || value === 'all_exclude_keep' ? value : 'all') as 'all' | 'all_exclude_keep' | 'keep4w';
    setDownloadMode(next);
  }

  // 비회원 사용자 데이터 저장 함수
  const saveNonMemberData = (data: CustomerRow[], date: Date) => {
    try {
      // 항상 최신 pseudoUserId 사용
      const currentPseudoUserId = getPseudoUserId();
      
      // 날짜를 문자열로 변환
      const formattedDate = format(date, "yyyy-MM-dd");
      
      // 저장하기 전에 데이터 로깅
      console.log(`[NewExcel:saveNonMemberData] 저장 시도 - ${data.length}개 행, 날짜: ${formattedDate}, pseudoUserId: ${currentPseudoUserId}`);
      
      // 데이터 유효성 검사
      const validatedData = data.map((row, index) => {
        // 닉네임 필드에 스토리지 키가 들어가는 경우 처리
        const nickname = row.nickname?.includes(`parsed_order_${currentPseudoUserId}`) ? "" : row.nickname;
        
        return {
          ...row,
          nickname, // nickname은 위에서 이미 한번 처리되었을 수 있으나, 안전하게 한번 더
          name: isEmptyFieldValue(row.name) ? "" : row.name,
          phone: isEmptyFieldValue(row.phone) ? "" : row.phone,
          address: isEmptyFieldValue(row.address) ? "" : row.address,
          deliveryNotes: isEmptyFieldValue(row.deliveryNotes || "") ? "" : (row.deliveryNotes || ""),
          productName: isEmptyFieldValue(row.productName || "") ? "" : (row.productName || ""),
          row_order: index // row_order 재설정
        };
      });
      
      // sessionStorage에 데이터 저장
      const storageKey = `parsed_order_${currentPseudoUserId}_${formattedDate}`;
      console.log(`[NewExcel:saveNonMemberData] 스토리지 키: ${storageKey}`);
      
      // 실제 저장 데이터 확인 (디버깅용)
      const dataToSave = JSON.stringify(validatedData);
      console.log(`[NewExcel:saveNonMemberData] 저장할 데이터 (일부): ${dataToSave.substring(0, 100)}...`);
      
      sessionStorage.setItem(storageKey, dataToSave);
      console.log(`[NewExcel:saveNonMemberData] 저장 완료 - ${validatedData.length}개 행 저장됨`);
      
      // 제대로 저장되었는지 확인 (디버깅용)
      const checkSaved = sessionStorage.getItem(storageKey);
      console.log(`[NewExcel:saveNonMemberData] 저장 확인 - 길이: ${checkSaved?.length || 0}`);
      
      // 하이라이트 날짜 업데이트
      updateNonMemberHighlightedDates(formattedDate);
    } catch (err) {
      console.error("[NewExcel:saveNonMemberData] 비회원 데이터 저장 중 오류:", err);
    }
  }

  // 비회원 사용자의 하이라이트 날짜 업데이트
  const updateNonMemberHighlightedDates = (dateStr: string) => {
    try {
      // 항상 최신 pseudoUserId 사용
      const currentPseudoUserId = getPseudoUserId();
      
      const storageKey = `highlighted_dates_${currentPseudoUserId}`;
      console.log(`[NewExcel:updateNonMemberHighlightedDates] 날짜 업데이트 시도: ${dateStr}, 키: ${storageKey}`);
      
      const savedDates = sessionStorage.getItem(storageKey);
      
      let dates: string[] = [];
      if (savedDates) {
        try {
          dates = JSON.parse(savedDates) as string[];
          console.log(`[NewExcel:updateNonMemberHighlightedDates] 기존 날짜 목록: ${dates.join(', ')}`);
        } catch (e) {
          console.error(`[NewExcel:updateNonMemberHighlightedDates] 기존 날짜 파싱 오류, 새로 시작:`, e);
          dates = [];
        }
      }
      
      // 새 날짜가 리스트에 없으면 추가
      if (!dates.includes(dateStr)) {
        dates.push(dateStr);
        
        // 저장하기 전 날짜 유효성 검사
        const validDates = dates.filter(d => {
          try {
            return !isNaN(new Date(d).getTime());
          } catch (e) {
            console.error(`[NewExcel:updateNonMemberHighlightedDates] 잘못된 날짜 형식 제외: ${d}`);
            return false;
          }
        });
        
        sessionStorage.setItem(storageKey, JSON.stringify(validDates));
        
        // 새로 렌더링용 하이라이트 업데이트
        const datesToHighlight = validDates.map(d => new Date(d));
        setHighlightedDates(datesToHighlight);
        
        console.log(`[NewExcel:updateNonMemberHighlightedDates] 날짜 추가 완료: ${dateStr}, 총 ${validDates.length}개 날짜`);
      } else {
        console.log(`[NewExcel:updateNonMemberHighlightedDates] 이미 존재하는 날짜: ${dateStr}`);
      }
    } catch (err) {
      console.error("[NewExcel:updateNonMemberHighlightedDates] 하이라이트 날짜 업데이트 중 오류:", err);
    }
  }

  // 로딩 UI
  if (isLoading) {
    return (
      <main className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-8 max-w-8xl">
          <div className="flex flex-col items-center justify-center h-[400px]">
            <Loader2 className="h-12 w-12 text-teal-500 animate-spin mb-4" />
            <p className="text-lg text-gray-600">
              데이터를 불러오는 중입니다...
            </p>
            {localStorage.getItem('migrationFlow') === 'in_progress' && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">회원가입 후 데이터를 이전하는 중입니다.</p>
                <p className="text-sm text-gray-500 mt-1">약간의 시간이 소요될 수 있습니다.</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    // 강제로 마이그레이션 진행중 상태 제거
                    localStorage.removeItem('migrationFlow');
                    localStorage.removeItem('migrationFocusDate');
                    clearMigrationCheck();
                    setMigrationCompleted(true);
                    if (currentUserId) {
                      setIsLoading(true);
                      fetchRowsByDate(currentUserId, selectedDate).finally(() => {
                        setIsLoading(false);
                      });
                    }
                  }}
                >
                  데이터 로드 다시 시도
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-100">
      <div className={`container mx-auto px-4 py-8 max-w-8xl ${currentUserIsAuthenticated ? 'pb-[230px] sm:pb-[240px]' : 'pb-[384px]'}`}>
        <div className="space-y-8">
          {/* 헤더 */}
          <div className="text-center space-y-1 mb-4">
            <h1 className="text-lg md:text-xl font-bold text-gray-800">고객으로부터 받은 답장을 자동으로 정리해드려요</h1>
            <p className="text-xs sm:text-sm text-gray-600 max-w-xl mx-auto">
              붙여넣기만 하면 닉네임, 이름, 전화번호, 주소, 배송메모를 자동으로 추출해드릴게요.
            </p>
            {!currentUserIsAuthenticated && (
              <p>
                <span
                  className="text-teal-600 font-medium underline cursor-pointer hover:text-teal-700 transition-colors ml-1"
                  onClick={() => openLoginModal()}
                >
                  로그인하시면 고객 정보가 계정에 저장됩니다.
                </span>
              </p>
            )}
          </div>

          {/* 테이블 */}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-3">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <h2 className="text-base md:text-lg font-medium flex items-center gap-2 whitespace-nowrap">
                  <span>정리된 고객 정보</span>
                  {rows.length > 0 && (
                    <span className="bg-gray-100 text-gray-700 text-xs md:text-sm py-0.5 px-2 rounded-full">
                      총 {rows.length}명의 고객
                    </span>
                  )}
                </h2>
                {currentUserIsAuthenticated && (
                  <div className="flex shrink-0 items-center gap-2 sm:gap-3 whitespace-nowrap border p-1.5 rounded-md bg-white shadow-sm">
                    <span className="text-xs sm:text-sm font-medium text-gray-600">컬럼 추가:</span>
                    <label className="flex items-center gap-1 cursor-pointer text-xs sm:text-sm hover:text-teal-700">
                      <input
                        type="checkbox"
                        className="form-checkbox h-3 w-3 sm:h-4 sm:w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-offset-0"
                        checked={parsingOptions.parseNickname}
                        onChange={(e) => setParsingOptions({ parseNickname: e.currentTarget.checked })}
                      />
                      닉네임
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer text-xs sm:text-sm hover:text-teal-700">
                      <input
                        type="checkbox"
                        className="form-checkbox h-3 w-3 sm:h-4 sm:w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-offset-0"
                        checked={parsingOptions.parseProductName}
                        onChange={(e) => setParsingOptions({ parseProductName: e.currentTarget.checked })}
                      />
                      상품명
                    </label>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto justify-center md:justify-end flex-wrap">
                <DatePicker
                  date={selectedDate}
                  setDate={setSelectedDate}
                  highlightedDates={highlightedDates}
                  onPrevDay={handlePrevDay}
                  onNextDay={handleNextDay}
                />
                {(currentUserIsAuthenticated || !showEmptyState) && (
                  <ToggleGroup
                    type="single"
                    value={downloadMode}
                    onValueChange={handleDownloadModeChange}
                    className="h-9 bg-gray-200 rounded-lg p-1"
                  >
                    <ToggleGroupItem 
                      value="all" 
                      aria-label="전체(선택된 날짜)"
                      className="text-xs px-3 h-8 rounded-md bg-transparent text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-indigo-700"
                    >
                      전체 (선택된 날짜)
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="all_exclude_keep"
                      aria-label="킵 제외(선택된 날짜)"
                      disabled={!currentUserIsAuthenticated}
                      title={currentUserIsAuthenticated ? undefined : '로그인 후 이용 가능합니다'}
                      className="text-xs px-3 h-8 rounded-md bg-transparent text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-indigo-700 disabled:opacity-50"
                    >
                      킵 제외 (선택된 날짜)
                    </ToggleGroupItem>
                    <ToggleGroupItem 
                      value="keep4w" 
                      aria-label="킵(선택된 날짜부터 최근 4주)"
                      disabled={!currentUserIsAuthenticated}
                      title={currentUserIsAuthenticated ? undefined : '로그인 후 이용 가능합니다'}
                      className="text-xs px-3 h-8 rounded-md bg-transparent text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-indigo-700 disabled:opacity-50"
                    >
                      킵만 (선택된 날짜부터 최근 4주)
                    </ToggleGroupItem>
                  </ToggleGroup>
                )}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="gap-2 text-xs bg-blue-500 text-white hover:bg-blue-600 shrink-0"
                      // staleSyncBusy: 일괄 반영 중 다운로드 차단 — 반영 확정 전 stale 값 수출 방지(합의 계약)
                      // isSortPrefLoading: 정렬 기준 로딩 전 다운로드 = 저장된 기준과 다른 순서로 출력될 수 있음
                      disabled={(downloadMode === 'keep4w' && isKeepDownloading) || staleSyncBusy || isSortPrefLoading}
                      title={isSortPrefLoading ? "순서 설정을 불러오는 중입니다." : undefined}
                    >
                      {downloadMode === 'keep4w' && isKeepDownloading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileDown className="h-4 w-4" />
                      )}
                      엑셀 다운로드
                      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-lg shadow-lg border border-gray-200 bg-white">
                    {savedTemplate ? (
                      <>
                        <DropdownMenuItem
                          className="text-sm px-3 py-2 cursor-pointer"
                          onClick={handleTemplateExcelDownload}
                        >
                          <FileDown className="h-4 w-4 mr-2 text-teal-500" />
                          {savedTemplate.name} 양식으로 다운로드
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-sm px-3 py-2 cursor-pointer"
                          onClick={handleUnifiedExcelDownload}
                        >
                          <FileDown className="h-4 w-4 mr-2 text-gray-400" />
                          기본 양식으로 다운로드
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-sm px-3 py-2 cursor-pointer"
                          onClick={handleOpenTemplateDialog}
                        >
                          <Settings className="h-4 w-4 mr-2 text-gray-400" />
                          택배사 양식 관리
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem
                          className="text-sm px-3 py-2 cursor-pointer"
                          onClick={handleUnifiedExcelDownload}
                        >
                          <FileDown className="h-4 w-4 mr-2 text-blue-500" />
                          기본 양식으로 다운로드
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-sm px-3 py-2 cursor-pointer"
                          onClick={handleOpenTemplateDialog}
                        >
                          <Settings className="h-4 w-4 mr-2 text-gray-400" />
                          내 택배사 양식 설정하기
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {currentUserIsAuthenticated && (
              <ShippingStaleSyncContainer
                userId={currentUserId}
                orderDate={format(selectedDate, "yyyy-MM-dd")}
                parseProductName={parsingOptions.parseProductName}
                // 이름만으론 "같은 이름으로 매핑 편집"을 감지 못함 — id+modifiedAt 포함
                templateKey={
                  savedTemplate
                    ? `${savedTemplate.id ?? ""}:${savedTemplate.modifiedAt ?? ""}:${savedTemplate.name}`
                    : null
                }
                refreshToken={staleSyncRefreshToken}
                onApplied={handleStaleSyncApplied}
                onReconciled={handleStaleSyncReconciled}
                onBusyChange={setStaleSyncBusy}
              />
            )}

            {selectedRowIds.size > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg mb-2 animate-fadeIn">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">
                    <span className="font-bold text-red-600">{selectedRowIds.size}</span>
                    <span className="text-gray-500">/{rows.length}</span>개 선택됨
                  </span>
                  <button
                    className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
                    onClick={() => setSelectedRowIds(new Set())}
                  >
                    선택 해제
                  </button>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowBulkDeleteDialog(true)}
                  className="gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  선택 삭제
                </Button>
              </div>
            )}

            {isLoading ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <div className="flex items-center justify-center h-[200px]">
                  <Loader2 className="h-6 w-6 text-teal-500 animate-spin" />
                </div>
              </div>
            ) : showEmptyState ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <div ref={emptyGuideRef} className="max-w-2xl mx-auto space-y-4">
                  <div className="bg-teal-50 rounded-full p-3 w-12 h-12 mx-auto flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-teal-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800">아직 추가된 고객 정보가 없어요</h3>
                  
                  {/* 가이드 영상 추가 */}
                  <div className="rounded-lg overflow-hidden my-6 border border-gray-200 w-full">
                    <video 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="w-full max-h-[280px] md:max-h-[340px] object-contain mx-auto"
                      src="/assets/excel-guide.mp4" 
                    >
                      <source src="/assets/excel-guide.mp4" type="video/mp4" />
                      브라우저가 비디오 태그를 지원하지 않습니다.
                    </video>
                  </div>
                  
                  <ol className="text-gray-600 text-left list-decimal pl-5 space-y-1">
                    <li>고객의 이름, 주소, 전화번호가 포함된 메시지를 그대로 복사하세요.</li>
                    <li>바로 아래에 있는 '새로운 고객 정보 추가하기' 창에 붙여넣기하세요.</li>
                    <li>자동정리하기 버튼을 눌러주세요.</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden w-full">
                <div className="overflow-x-auto">
                  <table ref={tableRef} className="w-full text-sm table-fixed min-w-[1200px]">
                    <colgroup>{/*
                      */}<col style={{ width: `${columnWidths.checkbox}%` }} />{/*
                      */}<col style={{ width: `${columnWidths.orderNum}%` }} />{/*
                      */}{parsingOptions.parseNickname && <col style={{ width: `${columnWidths.nickname}%` }} />}{/*
                      */}<col style={{ width: `${columnWidths.name}%` }} />{/*
                      */}<col style={{ width: `${columnWidths.phone}%` }} />{/*
                      */}<col style={{ width: `${columnWidths.address}%` }} />{/*
                      */}{parsingOptions.parseProductName && <col style={{ width: `${columnWidths.productName}%` }} />}{/*
                      */}<col style={{ width: `${columnWidths.deliveryNotes}%` }} />{/*
                      */}<col style={{ width: `${columnWidths.actions}%` }} />{/*
                    */}</colgroup>
                    <thead>
                      <tr className="text-white border-b-2 border-gray-300" style={{ backgroundColor: '#15B8A6' }}>
                        <th className="text-center py-2 px-1 font-medium text-xs sticky left-0 z-10"
                            style={{ backgroundColor: '#15B8A6' }}>
                          <div className="flex items-center justify-center min-h-[36px]">
                            <Checkbox
                              checked={headerCheckState}
                              onCheckedChange={handleSelectAll}
                              aria-label="모든 항목 선택"
                              className="border-white data-[state=checked]:bg-white data-[state=checked]:text-teal-600 data-[state=indeterminate]:bg-white data-[state=indeterminate]:text-teal-600"
                            />
                          </div>
                        </th>
                        <th className={`text-center py-2 font-medium relative border-r border-r-gray-300 text-xs ${currentUserIsAuthenticated ? 'px-0.5' : 'px-3'}`}>
                          {currentUserIsAuthenticated ? (
                            <ShippingSortHeaderMenu
                              sort={effectiveShippingSort}
                              status={preferenceStatus}
                              onChange={setShippingExcelSort}
                            />
                          ) : (
                            "순서"
                          )}
                          <div
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                            onMouseDown={(e) => handleResizeStart(e, 'orderNum')}
                          />
                        </th>
                        {parsingOptions.parseNickname && (
                          <th className="text-center py-2 px-3 font-medium relative border-r border-r-gray-300 text-xs">
                            닉네임
                            <div
                              className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                              onMouseDown={(e) => handleResizeStart(e, 'nickname')}
                            />
                          </th>
                        )}
                        <th className="text-center py-2 px-3 font-medium relative border-r border-r-gray-300 text-xs">
                          이름
                          <div
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                            onMouseDown={(e) => handleResizeStart(e, 'name')}
                          />
                        </th>
                        <th className="text-center py-2 px-3 font-medium relative border-r border-r-gray-300 text-xs">
                          전화번호
                          <div
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                            onMouseDown={(e) => handleResizeStart(e, 'phone')}
                          />
                        </th>
                        <th className="text-center py-2 px-3 font-medium relative border-r border-r-gray-300 text-xs">
                          주소
                          <div
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                            onMouseDown={(e) => handleResizeStart(e, 'address')}
                          />
                        </th>
                        {parsingOptions.parseProductName && (
                          <th className="text-center py-2 px-3 font-medium relative border-r border-r-gray-300 text-xs">
                            상품명
                            <div
                              className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                              onMouseDown={(e) => handleResizeStart(e, 'productName')}
                            />
                          </th>
                        )}
                        <th className="text-center py-2 px-3 font-medium relative border-r border-r-gray-300 text-xs">
                          배송메모
                          <div
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-teal-300 active:bg-teal-500"
                            onMouseDown={(e) => handleResizeStart(e, 'deliveryNotes')}
                          />
                        </th>
                        <th className="text-center py-2 px-3 font-medium text-xs">관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((row, index) => (
                        <tr
                          key={row.id}
                          className={`border-t ${
                            selectedRowIds.has(row.id)
                              ? "bg-teal-50 hover:bg-teal-100"
                              : newRowIndex === index
                                ? "bg-lime-100 animate-fadeIn hover:bg-lime-50"
                                : index % 2 === 0
                                  ? "bg-white hover:bg-gray-50"
                                  : "bg-gray-50 hover:bg-gray-100"
                          }`}
                        >
                          <td
                            className="py-0.5 px-1 align-middle text-center sticky left-0 z-[5] cursor-pointer"
                            style={{ backgroundColor: selectedRowIds.has(row.id)
                              ? '#f0fdfa'
                              : index % 2 === 0 ? '#ffffff' : '#f9fafb' }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSelectRow(row.id, !selectedRowIds.has(row.id))
                            }}
                          >
                            <div className="flex items-center justify-center min-h-[32px]">
                              <Checkbox
                                checked={selectedRowIds.has(row.id)}
                                onCheckedChange={(checked) => handleSelectRow(row.id, !!checked)}
                                aria-label={`${row.name || row.nickname || '행'} 선택`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </td>
                          <td className="py-0.5 px-3 align-middle border-r text-xs text-center font-medium">
                            {index + 1}
                          </td>
                          {parsingOptions.parseNickname && (
                            <td
                              className="py-0.5 px-3 align-middle cursor-pointer hover:bg-gray-50 border-r text-xs text-center"
                              onClick={() => editingRowId !== row.id && handleEditClick(row, "nickname")}
                            >
                              {editingRowId === row.id && editingField === "nickname" ? (
                                <input
                                  type="text"
                                  value={editFormData?.nickname || ""}
                                  onChange={(e) => handleEditInputChange(e, "nickname")}
                                  className="w-full p-0.5 border rounded text-xs text-center"
                                  autoFocus
                                  ref={inputRefs.nickname}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSaveClick();
                                    }
                                    if (e.key === "Escape") handleCancelClick();
                                  }}
                                  onBlur={handleSaveClick}
                                />
                              ) : row.nickname && keepNicknames.has(row.nickname) ? (
                                <span className="inline-flex min-w-0 max-w-full items-center justify-center gap-1">
                                  <span
                                    title="킵 고객"
                                    className="shrink-0 rounded-sm bg-red-100 px-1 text-[10px] font-semibold leading-4 text-red-800"
                                  >
                                    킵
                                  </span>
                                  <span className="min-w-0 break-all">{renderEmptyFieldValue(row.nickname, "nickname")}</span>
                                </span>
                              ) : (
                                renderEmptyFieldValue(row.nickname, "nickname")
                              )}
                            </td>
                          )}
                          <td
                            className="py-0.5 px-3 align-middle cursor-pointer hover:bg-gray-50 border-r text-xs text-center"
                            onClick={() => editingRowId !== row.id && handleEditClick(row, "name")}
                          >
                            {editingRowId === row.id && editingField === "name" ? (
                              <input
                                type="text"
                                value={editFormData?.name || ""}
                                onChange={(e) => handleEditInputChange(e, "name")}
                                className="w-full p-0.5 border rounded text-xs text-center"
                                autoFocus
                                ref={inputRefs.name}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveClick();
                                  }
                                  if (e.key === "Escape") handleCancelClick();
                                }}
                                onBlur={handleSaveClick}
                              />
                            ) : (
                              renderEmptyFieldValue(row.name, "name")
                            )}
                          </td>
                          <td
                            className="py-0.5 px-3 align-middle cursor-pointer hover:bg-gray-50 border-r text-xs text-center"
                            onClick={() => editingRowId !== row.id && handleEditClick(row, "phone")}
                          >
                            {editingRowId === row.id && editingField === "phone" ? (
                              <input
                                type="text"
                                value={editFormData?.phone || ""}
                                onChange={(e) => handleEditInputChange(e, "phone")}
                                className="w-full p-0.5 border rounded text-xs text-center"
                                autoFocus
                                ref={inputRefs.phone}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveClick();
                                  }
                                  if (e.key === "Escape") handleCancelClick();
                                }}
                                onBlur={handleSaveClick}
                              />
                            ) : (
                              renderEmptyFieldValue(row.phone, "phone")
                            )}
                          </td>
                          <td
                            className="py-0.5 px-3 align-middle cursor-pointer hover:bg-gray-50 border-r text-xs"
                            onClick={() => editingRowId !== row.id && handleEditClick(row, "address")}
                          >
                            {editingRowId === row.id && editingField === "address" ? (
                              <input
                                type="text"
                                value={editFormData?.address || ""}
                                onChange={(e) => handleEditInputChange(e, "address")}
                                className="w-full p-0.5 py-0.5 border rounded text-xs"
                                autoFocus
                                ref={inputRefs.address}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveClick();
                                  }
                                  if (e.key === "Escape") handleCancelClick();
                                }}
                                onBlur={handleSaveClick}
                              />
                            ) : (
                              <div className="w-full p-0.5 py-0.5 rounded text-xs break-words line-clamp-2">{renderEmptyFieldValue(row.address, "address")}</div>
                            )}
                          </td>
                          {parsingOptions.parseProductName && (
                            <td /* 상품명 td 위치 변경 */
                              className="py-0.5 px-3 align-middle cursor-pointer hover:bg-gray-50 border-r text-xs text-center"
                              onClick={() => editingRowId !== row.id && handleEditClick(row, "productName")}
                            >
                              {editingRowId === row.id && editingField === "productName" ? (
                                <textarea
                                  value={editFormData?.productName || ""}
                                  onChange={(e) => handleEditInputChange(e, "productName")}
                                  className="w-full p-0.5 border rounded text-xs text-center whitespace-pre-wrap"
                                  rows={2}
                                  autoFocus
                                  ref={inputRefs.productName}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSaveClick();
                                    }
                                    if (e.key === "Escape") handleCancelClick();
                                  }}
                                  onBlur={handleSaveClick}
                                />
                              ) : (
                                <div className="whitespace-pre-wrap break-words">
                                  {renderEmptyFieldValue(row.productName || "", "productName")}
                                </div>
                              )}
                            </td>
                          )}
                          <td
                            className="py-0.5 px-3 align-middle cursor-pointer hover:bg-gray-50 border-r text-xs text-center"
                            onClick={() => editingRowId !== row.id && handleEditClick(row, "deliveryNotes")}
                          >
                            {editingRowId === row.id && editingField === "deliveryNotes" ? (
                              <input
                                type="text"
                                value={editFormData?.deliveryNotes || ""}
                                onChange={(e) => handleEditInputChange(e, "deliveryNotes")}
                                className="w-full p-0.5 border rounded text-xs text-center"
                                autoFocus
                                ref={inputRefs.deliveryNotes}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveClick();
                                  }
                                  if (e.key === "Escape") handleCancelClick();
                                }}
                                onBlur={handleSaveClick}
                              />
                            ) : (
                              <div className="break-words line-clamp-2 text-center">{renderEmptyFieldValue(row.deliveryNotes, "deliveryNotes")}</div>
                            )}
                          </td>
                          <td className="py-0.5 px-3 align-middle text-xs text-center">
                            <div className="flex justify-center space-x-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6 min-h-0 min-w-0" title="삭제" onClick={() => deleteRow(row.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 하단 고정 입력 카드 */}
      <div className={`fixed left-0 right-0 bg-white border-t shadow-lg z-[60] ${currentUserIsAuthenticated ? 'bottom-0 sm:bottom-0' : 'bottom-[130px] sm:bottom-[82px]'}`}>
        <div className="container mx-auto px-2 sm:px-4 max-w-7xl">
          <Card className="p-2 sm:p-3 border-0">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5 text-teal-500" />
                <h2 className="text-sm sm:text-base font-medium">새로운 고객 정보 추가하기</h2>
              </div>

              <Textarea
                placeholder={`고객으로부터 받은 답장을 여기에 그대로 붙여넣어보세요.`}
                className="min-h-[100px] sm:min-h-[120px] text-sm sm:text-base whitespace-pre-line"
                value={customerText}
                onChange={handleTextChange}
              />

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex justify-center mt-2"> {/* mt-2 추가하여 버튼과 Textarea 간격 확보 */}
                <Button
                  onClick={parseCustomerText}
                  disabled={isProcessing}
                  className="bg-teal-500 hover:bg-teal-600 text-white gap-2 text-sm px-3 py-1.5 sm:px-4 sm:py-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                      <span className="text-xs sm:text-sm">정리 중...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="text-xs sm:text-sm">자동 정리하기</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* 비회원 사용 제한 다이얼로그 */}
      <AlertDialog open={showNonMemberLimitDialog} onOpenChange={setShowNonMemberLimitDialog}>
        <AlertDialogContent className="bg-white rounded-xl border-0 shadow-lg max-w-md mx-auto">
          <AlertDialogHeader className="space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50">
              <IconLock className="h-8 w-8 text-teal-500" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-center text-gray-900">
              오늘의 무료 사용 횟수를 모두 사용하셨습니다
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-base">
                <div className="p-4 rounded-lg bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100">
                  <p className="text-gray-700">
                    비회원은 자동 정리 기능을 하루 3회까지 이용할 수 있어요.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <IconCheck className="h-5 w-5 text-teal-500" />
                  </div>
                  <p className="text-gray-700">
                    <strong>1초만에 회원가입</strong> 후 로그인하면 넉넉하게 사용할 수 있어요.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <IconCheck className="h-5 w-5 text-teal-500" />
                  </div>
                  <p className="text-gray-700">
                    로그인된 계정에 배송 정보도 자동으로 저장돼요.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col space-y-2 sm:space-y-0 pt-2">
            <AlertDialogAction
              className="w-full bg-teal-500 hover:bg-teal-600 text-white shadow-sm py-2.5"
              onClick={() => {
                setShowNonMemberLimitDialog(false);
                openLoginModal();
              }}
            >
              로그인하기
            </AlertDialogAction>
            <AlertDialogCancel className="w-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 mt-2">
              다음에 할게요
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showUsageLimitDialog} onOpenChange={setShowUsageLimitDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-xl font-semibold text-gray-900">이번 달 사용 한도 도달</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-base">
                <p className="text-gray-700">
                  현재 구독 플랜의 이번 달 사용 가능한 횟수를 모두 소진하셨습니다.
                </p>
                <p className="text-gray-700">
                  더 많은 사용을 원하시면 스탠다드 또는 프리미엄 플랜으로 업그레이드해 주세요.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    • 스탠다드 플랜: 월 150건 사용 가능
                    <br />
                    • 프리미엄 플랜: 무제한 사용 가능
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="bg-gray-100 hover:bg-gray-200 text-gray-700">닫기</AlertDialogCancel>
            <AlertDialogAction
              className="bg-teal-500 hover:bg-teal-600 text-white"
              onClick={() => {
                setShowUsageLimitDialog(false);
                window.location.href = '/mypage';
              }}
            >
              플랜 변경하기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 일괄 삭제 확인 Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={(open) => {
        if (!isBulkDeleting) setShowBulkDeleteDialog(open)
      }}>
        <AlertDialogContent className="bg-white rounded-xl shadow-lg max-w-md mx-auto">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-lg font-semibold text-gray-900">
              선택한 항목을 삭제하시겠습니까?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-600">
              선택된 <span className="font-semibold text-red-600">{selectedRowIds.size}개</span>의
              고객 정보가 영구적으로 삭제됩니다.
              <br />
              이 작업은 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <AlertDialogCancel disabled={isBulkDeleting}>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={(e) => {
                e.preventDefault()
                handleBulkDelete()
              }}
              disabled={selectedRowIds.size === 0 || isBulkDeleting}
            >
              {isBulkDeleting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />삭제 중...</>
              ) : (
                `${selectedRowIds.size}개 삭제하기`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 택배사 양식 설정 Dialog */}
      <DeliveryTemplateManager
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        savedTemplate={savedTemplate}
        onSaveTemplate={async (template) => {
          if (!currentUserId) throw new Error('로그인이 필요합니다.')
          const saved = await deliveryTemplateService.saveTemplate(currentUserId, template)
          setSavedTemplate(saved)
        }}
        onDeleteTemplate={async () => {
          if (!currentUserId || !savedTemplate?.id) throw new Error('삭제할 양식이 없습니다.')
          await deliveryTemplateService.deleteTemplate(currentUserId, savedTemplate.id)
          setSavedTemplate(null)
        }}
      />
    </main>
  )
}

const IconLock = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const IconCheck = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);